import React from "react";
import { createArea, updatedArea } from "./area-factory-3d";
import Translator from "../../translator/translator";

let translator = new Translator();

/**
 * Build a solid room fill path from the detected cycle order.
 * If the cycle is self-crossing (half-white shards), fall back to the
 * axis-aligned bounds of the room so the interior stays solid white.
 */
function buildRoomFillPath(element, layer) {
  const pts = [];
  element.vertices.forEach((vertexID) => {
    const vertex = layer.vertices.get(vertexID);
    if (vertex) pts.push({ x: vertex.x, y: vertex.y });
  });
  if (pts.length < 3) return null;

  // Signed area (shoelace). Near-zero vs bbox ⇒ self-crossing / bad order.
  let signed = 0;
  for (let i = 0; i < pts.length; i++) {
    const j = (i + 1) % pts.length;
    signed += pts[i].x * pts[j].y - pts[j].x * pts[i].y;
  }
  const absArea = Math.abs(signed / 2);

  let minX = pts[0].x;
  let maxX = pts[0].x;
  let minY = pts[0].y;
  let maxY = pts[0].y;
  for (let i = 1; i < pts.length; i++) {
    minX = Math.min(minX, pts[i].x);
    maxX = Math.max(maxX, pts[i].x);
    minY = Math.min(minY, pts[i].y);
    maxY = Math.max(maxY, pts[i].y);
  }
  const bboxArea = Math.max(0, maxX - minX) * Math.max(0, maxY - minY);

  // Bad cycle: fill the room box (still correct for normal rectangular rooms).
  if (bboxArea > 1 && absArea < bboxArea * 0.35) {
    return (
      "M" +
      minX +
      " " +
      minY +
      " L" +
      maxX +
      " " +
      minY +
      " L" +
      maxX +
      " " +
      maxY +
      " L" +
      minX +
      " " +
      maxY +
      " Z"
    );
  }

  // Good cycle: draw in detect order; flip if clockwise so fill is solid.
  const ordered = signed > 0 ? pts.slice().reverse() : pts;
  let path = "";
  ordered.forEach((vertex, ind) => {
    path += (ind ? "L" : "M") + vertex.x + " " + vertex.y + " ";
  });
  path += "Z";
  return path;
}

export default function AreaFactory(name, info, textures) {
  let areaElement = {
    name,
    prototype: "areas",
    info: {
      ...info,
      visibility: {
        catalog: false,
        layerElementsVisible: false,
      },
    },
    properties: {
      roomName: {
        label: "Room Name",
        type: "string",
        defaultValue: "",
      },
      patternColor: {
        label: translator.t("color"),
        type: "color",
        defaultValue: "#fff",
      },
      opacity: {
        label: "opacity",
        type: "slider",
        min: 20,
        max: 100,
        defaultValue: 100,
      },
      deleteButton: {
        label: "Delete Room",
        type: "button",
        actionType: "delete-room",
        confirmationAlertMessage: "Are you sure you want to delete the room?",
        defaultValue: "",
      },
      // thickness: {
      //   label: translator.t('thickness'),
      //   type: 'length-measure',
      //   defaultValue: {
      //     length: 0,
      //   }
      // }
    },
    render2D: function (element, layer, state) {
      // Solid white room fill only — ignore hole cutouts in 2D (they caused
      // transparent rooms). Split/detect already keeps rooms independent.
      const path = buildRoomFillPath(element, layer);
      if (!path) {
        return <g />;
      }

      return (
        <path
          d={path}
          fill="#ffffff"
          fillOpacity={1}
          opacity={1}
          fillRule="nonzero"
          stroke="none"
        />
      );
    },

    render3D: function (element, layer, scene) {
      return createArea(element, layer, scene, textures);
    },

    updateRender3D: (
      element,
      layer,
      scene,
      mesh,
      oldElement,
      differences,
      selfDestroy,
      selfBuild
    ) => {
      return updatedArea(
        element,
        layer,
        scene,
        textures,
        mesh,
        oldElement,
        differences,
        selfDestroy,
        selfBuild
      );
    },
  };

  if (textures && textures !== {}) {
    let textureValues = { none: "None" };

    for (let textureName in textures) {
      textureValues[textureName] = textures[textureName].name;
    }

    // areaElement.properties.texture = {
    //   label: translator.t('texture'),
    //   type: 'enum',
    //   defaultValue: 'none',
    //   values: textureValues
    // };
  }

  return areaElement;
}
